import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";

import Landing from "@/pages/Landing";
import Book from "@/pages/Book";
import Login from "@/pages/Login";
import Customer from "@/pages/Customer";
import Driver from "@/pages/Driver";
import Passenger from "@/pages/Passenger";
import Dispatcher from "@/pages/Dispatcher";
import ReservationDetail from "@/pages/ReservationDetail";
import Lookup from "@/pages/Lookup";
import Feedback from "@/pages/Feedback";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import Track from "@/pages/Track";
import Series from "@/pages/Series";
import Corporate from "@/pages/Corporate";
import CorporateSignup from "@/pages/CorporateSignup";
import PayInvoice from "@/pages/PayInvoice";
import DesignPreview from "@/pages/DesignPreview";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DriverTrip from "@/pages/DriverTrip";
import DriverOnboarding from "@/pages/DriverOnboarding";
import DriveWithUs from "@/pages/DriveWithUs";
import DriverLogin from "@/pages/DriverLogin";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Events from "@/pages/Events";
import RentalSign from "@/pages/RentalSign";
import AiChatWidget from "@/components/AiChatWidget";

const CUSTOMER_CHAT_PATHS = ["/", "/book", "/events", "/lookup", "/track"];

/** Reset scroll position on every route change (mobile browsers keep the old offset). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id during render (not in useEffect) — prevents race with /auth/me
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback/>;
  }
  return (
    <>
    <ScrollToTop/>
    <Routes>
      <Route path="/" element={<Landing/>}/>
      <Route path="/book" element={<Book/>}/>
      <Route path="/events" element={<Events/>}/>
      <Route path="/lookup" element={<Lookup/>}/>
      <Route path="/track" element={<Track/>}/>
      <Route path="/pay-invoice/:token" element={<PayInvoice/>}/>
      <Route path="/design-preview" element={<DesignPreview/>}/>
      <Route path="/driver-trip/:token" element={<DriverTrip/>}/>
      <Route path="/driver-onboarding/:token" element={<DriverOnboarding/>}/>
      <Route path="/drive-with-us" element={<DriveWithUs/>}/>
      <Route path="/driver-login" element={<DriverLogin/>}/>
      <Route path="/phone-login" element={<DriverLogin audience="customer"/>}/>
      <Route path="/rental-sign/:token" element={<RentalSign/>}/>
      <Route path="/privacy" element={<Privacy/>}/>
      <Route path="/terms" element={<Terms/>}/>
      <Route path="/feedback" element={<Feedback/>}/>
      <Route path="/login" element={<Login/>}/>
      <Route path="/forgot-password" element={<ForgotPassword/>}/>
      <Route path="/reset-password" element={<ResetPassword/>}/>
      <Route path="/dashboard" element={<Dashboard/>}/>
      <Route path="/customer" element={<Customer/>}/>
      <Route path="/customer/:tab" element={<Customer/>}/>
      <Route path="/series" element={<Series/>}/>
      <Route path="/corporate" element={<Corporate/>}/>
      <Route path="/corporate/signup" element={<CorporateSignup/>}/>

      <Route path="/driver" element={<Driver/>}/>
      <Route path="/passenger" element={<Passenger/>}/>
      <Route path="/dispatcher" element={<Dispatcher/>}/>
      <Route path="/dispatcher/reservation/:bookingId" element={<ReservationDetail/>}/>
      <Route path="*" element={<Landing/>}/>
    </Routes>
    {CUSTOMER_CHAT_PATHS.includes(location.pathname) && <AiChatWidget mode="customer"/>}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter/>
          <Toaster
            position="top-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "#0A0A0A",
                border: "1px solid #27272A",
                color: "#F5F2EB",
                fontFamily: "Outfit, sans-serif",
              }
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
